import { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Badge,
  Col,
  Empty,
  Flex,
  Segmented,
  Space,
  Tag,
  Typography,
} from "antd";
import { MailOutlined } from "@ant-design/icons";
import { Bubble, Sender } from "@ant-design/x";
import { Card } from "@rumbledirect/ui";
import type { Dossier } from "@rumble-it/common-lib/types/models/Dossier.interface";
import { useChatConversations } from "../../../../lib/hooks/useChat";
import { chatChannelConfig } from "../../chat/data/chatChannelConfig";
import { ChatConversationView } from "../../chat/components/ChatConversationView";
import { ChatConversationListItem } from "../../chat/components/ChatConversationListItem";
import { ChatDossierComposer } from "../../chat/components/ChatDossierComposer";
import {
  type MessageChannel,
  type StakeholderConversation,
  buildMockConversations,
  channelConfig,
} from "../data/communicationData";

const { Text } = Typography;

// ponytail: demo override - claims Communicatie tab reverted to hardcoded
// mock data; flip to false to restore the real chat inbox wiring.
const USE_MOCK_COMMUNICATION_TAB = true;

const formatTime = (iso: string) => {
  const date = new Date(iso);
  const now = new Date();
  const diffDays = Math.floor(
    (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24),
  );

  if (diffDays === 0) {
    return date.toLocaleTimeString("nl-NL", {
      hour: "2-digit",
      minute: "2-digit",
    });
  }
  if (diffDays === 1) return "Gisteren";
  if (diffDays < 7) return `${diffDays} dagen geleden`;
  return date.toLocaleDateString("nl-NL", { day: "numeric", month: "short" });
};

const ChannelTag = ({ channel }: { channel: MessageChannel }) => {
  const config = channelConfig[channel];
  return (
    <Tag color={config.color} style={{ fontSize: 11, margin: 0 }}>
      {config.label}
    </Tag>
  );
};

const MockConversationListItem = ({
  conversation,
  isActive,
  onClick,
}: {
  conversation: StakeholderConversation;
  isActive: boolean;
  onClick: () => void;
}) => {
  const lastMessage = conversation.messages[conversation.messages.length - 1];

  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left cursor-pointer p-3 border-b transition-all ${
        isActive
          ? "bg-primary-900 border-l-2 border-l-primary-600"
          : "hover:bg-gray-50"
      }`}
    >
      <Flex align="start" gap={12}>
        <Badge count={conversation.unreadCount} size="small">
          <Avatar
            style={{
              backgroundColor: isActive
                ? "var(--ant-color-primary)"
                : "#8c8c8c",
            }}
          >
            {conversation.name.charAt(0)}
          </Avatar>
        </Badge>
        <Flex vertical style={{ flex: 1, minWidth: 0 }}>
          <Flex justify="space-between" align="center">
            <Text strong={conversation.unreadCount > 0}>
              {conversation.name}
            </Text>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {lastMessage ? formatTime(lastMessage.timestamp) : ""}
            </Text>
          </Flex>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {conversation.role}
          </Text>
          {lastMessage ? (
            <Flex align="center" gap={4} style={{ marginTop: 4 }}>
              <ChannelTag channel={lastMessage.channel} />
              <Text type="secondary" ellipsis style={{ fontSize: 12, flex: 1 }}>
                {lastMessage.content}
              </Text>
            </Flex>
          ) : null}
        </Flex>
      </Flex>
    </button>
  );
};

const MockCommunicationTab = ({ data }: { data: Dossier | undefined }) => {
  const driverName =
    data?.driver?.person?.fullName ?? data?.driver?.fullName ?? "Bestuurder";
  const servicePartnerName =
    data?.servicePartners?.[0]?.name ?? "Herstelbedrijf";

  const conversations = useMemo(
    () => buildMockConversations(driverName, servicePartnerName),
    [driverName, servicePartnerName],
  );

  const [activeConversation, setActiveConversation] = useState(
    conversations[0].key,
  );
  const [inputValue, setInputValue] = useState("");

  const selectedConversation = conversations.find(
    (c) => c.key === activeConversation,
  );

  return (
    <Flex gap={20} className="w-full !px-0">
      <Col className="!px-0" style={{ width: 360, minWidth: 360 }}>
        <Card title="Berichtenbox" className="h-full">
          <div className="-mx-6 -mb-6">
            {conversations.map((conversation) => (
              <MockConversationListItem
                key={conversation.key}
                conversation={conversation}
                isActive={conversation.key === activeConversation}
                onClick={() => setActiveConversation(conversation.key)}
              />
            ))}
          </div>
        </Card>
      </Col>

      <Col flex="auto" className="!px-0">
        {selectedConversation ? (
          <Card
            title={
              <Flex align="center" gap={12}>
                <Avatar style={{ backgroundColor: "var(--ant-color-primary)" }}>
                  {selectedConversation.name.charAt(0)}
                </Avatar>
                <Flex vertical>
                  <Text strong>{selectedConversation.name}</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {selectedConversation.role}
                  </Text>
                </Flex>
                <Space style={{ marginLeft: "auto" }}>
                  {Object.entries(channelConfig).map(([key, config]) => (
                    <Tag key={key} color={config.color}>
                      {config.label}
                    </Tag>
                  ))}
                </Space>
              </Flex>
            }
            className="h-full"
          >
            <Flex
              vertical
              style={{ height: "calc(100vh - 340px)", minHeight: 400 }}
            >
              <div style={{ flex: 1, overflowY: "auto", paddingBottom: 16 }}>
                <Bubble.List
                  items={selectedConversation.messages.map((msg) => ({
                    key: msg.id,
                    role: msg.isOutgoing ? "user" : "ai",
                    content: msg.content,
                    avatar: (
                      <Avatar
                        style={{
                          backgroundColor: msg.isOutgoing
                            ? "var(--ant-color-primary)"
                            : "#8c8c8c",
                        }}
                      >
                        {msg.sender.charAt(0)}
                      </Avatar>
                    ),
                    header: (
                      <Flex align="center" gap={8}>
                        <Text
                          strong
                          style={{ fontSize: 12, whiteSpace: "nowrap" }}
                        >
                          {msg.sender}
                        </Text>
                        <ChannelTag channel={msg.channel} />
                      </Flex>
                    ),
                    footer: (
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {new Date(msg.timestamp).toLocaleString("nl-NL", {
                          day: "numeric",
                          month: "short",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </Text>
                    ),
                  }))}
                  role={{
                    user: { placement: "end" },
                    ai: { placement: "start" },
                  }}
                  style={{ height: "100%" }}
                />
              </div>

              <Sender
                value={inputValue}
                onChange={setInputValue}
                placeholder={`Bericht naar ${selectedConversation.name}...`}
                onSubmit={() => setInputValue("")}
                actions={(_, info) => info.components.SendButton}
                submitType="enter"
              />
            </Flex>
          </Card>
        ) : null}
      </Col>
    </Flex>
  );
};

const RealChatCommunicationTab = ({ data }: { data: Dossier | undefined }) => {
  const dossierId = data?.id;
  const conversationsQuery = useChatConversations(
    dossierId ? { dossierId } : {},
    !!dossierId,
  );
  const conversations = conversationsQuery.data?.member ?? [];

  const [activeConversationId, setActiveConversationId] = useState<
    string | null
  >(null);

  useEffect(() => {
    if (!activeConversationId && conversations.length > 0) {
      setActiveConversationId(conversations[0].id);
    }
  }, [conversations, activeConversationId]);

  if (conversationsQuery.isLoading) {
    return <Card className="w-full" loading />;
  }

  // No conversations yet: hide the list column and offer a channel choice to start one.
  if (conversations.length === 0) {
    return (
      <Card title="Nieuw bericht" className="w-full">
        <Flex vertical gap={16} style={{ maxWidth: 520 }}>
          <Flex vertical gap={4}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Verstuur via
            </Text>
            <Segmented
              value="whatsapp"
              options={[
                {
                  label: (
                    <Flex align="center" gap={6}>
                      {chatChannelConfig.whatsapp.icon}
                      WhatsApp
                    </Flex>
                  ),
                  value: "whatsapp",
                },
                {
                  label: (
                    <Flex align="center" gap={6}>
                      <MailOutlined />
                      E-mail
                    </Flex>
                  ),
                  value: "email",
                  disabled: true,
                },
              ]}
            />
          </Flex>
          {dossierId ? <ChatDossierComposer dossierId={dossierId} /> : null}
        </Flex>
      </Card>
    );
  }

  return (
    <Flex gap={16} className="w-full !px-0">
      <Col className="!px-0" style={{ width: 320, minWidth: 280 }}>
        <Card title="Berichtenbox" className="h-full">
          <div className="-mx-6 -mb-6 border-t">
            {conversations.map((conversation) => (
              <ChatConversationListItem
                key={conversation.id}
                conversation={conversation}
                isActive={conversation.id === activeConversationId}
                onClick={() => setActiveConversationId(conversation.id)}
              />
            ))}
          </div>
        </Card>
      </Col>

      <Col flex="auto" className="!px-0">
        <Card
          className="h-full"
          styles={{ body: { height: "calc(100vh - 300px)", minHeight: 400 } }}
        >
          {activeConversationId ? (
            <ChatConversationView conversationId={activeConversationId} />
          ) : (
            <Flex align="center" justify="center" style={{ height: "100%" }}>
              <Empty description="Selecteer een gesprek" />
            </Flex>
          )}
        </Card>
      </Col>
    </Flex>
  );
};

export const ClaimDossierCommunicationTab = ({
  data,
}: {
  data: Dossier | undefined;
}) =>
  USE_MOCK_COMMUNICATION_TAB ? (
    <MockCommunicationTab data={data} />
  ) : (
    <RealChatCommunicationTab data={data} />
  );
